/**
 * Decor — fontes iluminadas da cidade:
 *   • PRAÇA central (quadra sem prédio): fonte + placa (billboard) com o logo do
 *     WhatsApp + bancos ao redor. Nem toda cidade tem praça (Mexican Town não tem).
 *   • JARDIM de cada SUPERQUADRA (cityLayout.DISTRICTS): a MESMA fonte da praça,
 *     só que na cor do distrito (água/halo/bulbo) e um pouco maior, para preencher
 *     o círculo de grama do miolo.
 * As árvores são geradas em Trees/cityLayout.
 */

import * as THREE from 'three';
import { COLORS, mixHex } from '../config.js';
import { DISTRICTS, ROUNDABOUT } from './cityLayout.js';
import whatsappWhiteUrl from '../assets/whatsappGlyphWhite.png';

// Nome (outdoor) de cada distrito, por `id` (ver cities.js → districts[].id).
const DISTRICT_NAMES = {
  1: 'Financial',
  2: 'Consumer Packaged Goods', // TROCADO com o 4 (2026-08-10)
  3: 'Auto, Travel, Education & Real Estate',
  4: 'Retail & E-commerce' // TROCADO com o 2
};

export function createDecor() {
  const group = new THREE.Group();
  group.name = 'decor';

  if (ROUNDABOUT) {
    const cx = ROUNDABOUT.x;
    const cz = ROUNDABOUT.z;

    group.add(makeFountain(cx, cz));
    group.add(makeWhatsAppSign(cx, cz - 9)); // placa ao fundo, voltada à câmera

    // Bancos nos 4 cantos do largo, encarando a fonte
    for (const [sx, sz] of [
      [4.4, 4.4],
      [-4.4, 4.4],
      [4.4, -4.4],
      [-4.4, -4.4]
    ]) {
      const bench = makeBench();
      bench.position.set(cx + sx, 0, cz + sz);
      bench.rotation.y = Math.atan2(-sx, -sz); // encara o centro
      group.add(bench);
    }
  }

  // Centro do distrito: SEM fonte/praça (cliente pediu p/ remover — cada distrito virou uma
  // superquadra 2×3 só com os canteiros dos prédios). makeFountain segue definido p/ o ROUNDABOUT
  // de outras cidades. (Banner/bandeira do nome também removidos antes — funções mantidas p/ reuso.)
  // for (const d of DISTRICTS) group.add(makeFountain(d.axisX, d.axisZ, d.color));

  return group;
}

/* ------------------- BANDEIRAS de nome do distrito ------------------- */

/** Quebra um nome em 2 linhas BALANCEADAS (minimiza a largura da linha mais larga). */
function wrapTwoLines(text, meas) {
  const words = text.split(/\s+/);
  if (words.length < 2) return [text];
  let best = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
    const mx = Math.max(meas.measureText(a).width, meas.measureText(b).width);
    if (mx < bestMax) { bestMax = mx; best = i; }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

/** Textura da BANDEIRA: campo na cor do distrito + nome branco bold (contorno escuro).
 *  ALTURA DA LETRA CONSTANTE — nome longo quebra em 2 linhas (canvas 2× mais alto). */
function makeFlagTexture(name, tint) {
  const FS = 96;                       // altura da letra (px) — CONSTANTE em todas as bandeiras
  const LH = Math.round(FS * 1.25);    // altura de linha (com leading)
  const PADX = 46;
  const font = `900 ${FS}px Inter, Arial, sans-serif`;
  const meas = document.createElement('canvas').getContext('2d');
  meas.font = font;
  const MAXW = 780; // largura (px) acima da qual quebra em 2 linhas
  const lines = meas.measureText(name).width <= MAXW ? [name] : wrapTwoLines(name, meas);
  const textW = Math.max(...lines.map((l) => meas.measureText(l).width));
  const cw = Math.ceil(textW + PADX * 2);
  const ch = lines.length * LH;        // SEM padding vertical fixo → altura ∝ nº de linhas (letra constante)
  const canvas = document.createElement('canvas');
  canvas.width = cw; canvas.height = ch;
  const c = canvas.getContext('2d');
  c.fillStyle = tint; c.fillRect(0, 0, cw, ch);                 // campo na cor do distrito
  c.strokeStyle = 'rgba(255,255,255,0.92)'; c.lineWidth = 9; c.strokeRect(5, 5, cw - 10, ch - 10);
  c.font = font; c.textAlign = 'center'; c.textBaseline = 'middle';
  c.lineJoin = 'round'; c.lineWidth = FS * 0.16; c.strokeStyle = 'rgba(0,0,0,0.6)'; // contorno escuro
  c.fillStyle = '#ffffff';
  lines.forEach((l, i) => { const y = LH * (i + 0.5); c.strokeText(l, cw / 2, y); c.fillText(l, cw / 2, y); });
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return { tex, aspect: cw / ch, lines: lines.length };
}

/**
 * NÃO USADA ATUALMENTE (cliente achou as bandeiras ruins — removidas da main.js; mantida p/
 * referência caso a ideia de mastro+bandeira seja revisitada).
 *
 * MASTRO + BANDEIRA com o nome de cada distrito, no centro da fonte. A bandeira TREMULA
 * (onda senoidal nos vértices, amplitude crescendo da haste p/ a ponta livre). Fica FORA do
 * batchStatic (precisa animar) → main.js adiciona o grupo e chama `update(dt)` no loop.
 * Altura de letra CONSTANTE; nome longo quebra em 2 linhas → bandeira com o DOBRO da altura.
 */
export function createDistrictFlags() {
  const group = new THREE.Group();
  group.name = 'districtFlags';
  const mastMat = new THREE.MeshStandardMaterial({ color: '#2a3630', roughness: 0.6, metalness: 0.5 });
  const finialMat = new THREE.MeshStandardMaterial({ color: '#e8c869', roughness: 0.4, metalness: 0.6, emissive: new THREE.Color('#e8c869'), emissiveIntensity: 0.2 });
  const MAST_TOP = 14;     // topo do mastro (mesma altura em todos)
  const LINE_WH = 2.5;     // altura FÍSICA por linha (2 linhas = 5.0 = o dobro) — bandeiras GRANDES p/ legibilidade
  const anim = [];
  for (const d of DISTRICTS) {
    const name = DISTRICT_NAMES[d.id];
    if (!name) continue;
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, MAST_TOP, 12), mastMat);
    mast.position.set(d.axisX, MAST_TOP / 2, d.axisZ); mast.castShadow = true; group.add(mast);
    const finial = new THREE.Mesh(new THREE.SphereGeometry(0.32, 14, 12), finialMat);
    finial.position.set(d.axisX, MAST_TOP + 0.22, d.axisZ); group.add(finial);
    const { tex, aspect, lines } = makeFlagTexture(name, d.color);
    const FH = lines * LINE_WH;        // 2 linhas ⇒ altura dobrada (letra constante)
    const FW = FH * aspect;
    const geo = new THREE.PlaneGeometry(FW, FH, 26, Math.max(3, lines * 3));
    const mat = new THREE.MeshStandardMaterial({ map: tex, emissiveMap: tex, emissive: new THREE.Color('#ffffff'), emissiveIntensity: 0.55, roughness: 0.6, side: THREE.DoubleSide });
    const flag = new THREE.Mesh(geo, mat);
    flag.position.set(d.axisX + 0.15 + FW / 2, MAST_TOP - FH / 2, d.axisZ); // topo alinhado ao topo do mastro
    flag.castShadow = true;
    group.add(flag);
    anim.push({ geo, half: FW / 2 });
  }
  let time = 0;
  return {
    group,
    update(dt) {
      time += dt;
      for (const a of anim) {
        const pos = a.geo.attributes.position;
        for (let i = 0; i < pos.count; i++) {
          const x = pos.getX(i), y = pos.getY(i);
          const frac = (x + a.half) / (2 * a.half); // 0 na haste → 1 na ponta livre
          const z = (Math.sin(x * 1.35 - time * 4.5) * 0.5 + Math.sin(y * 1.8 + time * 3.2) * 0.14) * frac;
          pos.setZ(i, z);
        }
        pos.needsUpdate = true;
        a.geo.computeVertexNormals();
      }
    }
  };
}

/* ------------------------------ fonte ------------------------------ */

export function makeFountain(cx, cz, tint = null) {
  const district = tint != null; // fonte do bairro (com jato colorido) x praça (original)
  const g = new THREE.Group();
  g.position.set(cx, 0, cz);

  // COR DO DISTRITO (`tint`): entra só no que BRILHA — a água, o bulbo e o halo no
  // chão. A pedra continua cinza, de propósito: é ela que dá o contraste. Sem `tint`
  // (a praça das outras cidades) tudo fica exatamente como era.
  const stoneA = new THREE.MeshStandardMaterial({ color: '#8d9087', roughness: 0.9 });
  const stoneB = new THREE.MeshStandardMaterial({ color: '#6f7269', roughness: 0.9 });
  const water = new THREE.MeshStandardMaterial({
    color: tint || '#3ddc84',
    emissive: new THREE.Color(tint || COLORS.glow),
    emissiveIntensity: 0.95,
    roughness: 0.2,
    metalness: 0.3
  });

  // Bacia
  const basin = new THREE.Mesh(new THREE.CylinderGeometry(4, 4.3, 1.0, 36), stoneA);
  basin.position.y = 0.5;
  basin.castShadow = true;
  basin.receiveShadow = true;
  g.add(basin);

  // Água (espelho)
  const pool = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 3.5, 0.5, 36), water);
  pool.position.y = 0.85;
  g.add(pool);

  // Pilar central
  const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.0, 1.8, 18), stoneB);
  pillar.position.y = 1.5;
  pillar.castShadow = true;
  g.add(pillar);

  // Taça superior
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 0.6, 0.5, 22), stoneA);
  bowl.position.y = 2.45;
  bowl.castShadow = true;
  g.add(bowl);

  // MIOLO — o MESMO da praça nas duas variantes (o usuário pediu para a fonte do
  // bairro usar o modelo da quadra-parque): disco de água no topo da taça + bulbo
  // aceso. No bairro ele sai na cor do distrito, CLAREADA — a cor cheia não cruza o
  // limiar do bloom e o bulbo ficaria opaco em vez de brilhar.
  const jet = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 0.3, 18), water);
  jet.position.y = 2.75;
  g.add(jet);
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(1.3, 16, 16),
    new THREE.MeshBasicMaterial({
      color: district ? mixHex(tint, '#ffffff', 0.55) : COLORS.glow,
      transparent: true,
      opacity: 0.5
    })
  );
  glow.position.y = 3.0;
  g.add(glow);

  // Halo no chão
  const halo = new THREE.Mesh(
    new THREE.CircleGeometry(5.6, 40),
    new THREE.MeshBasicMaterial({
      color: tint || COLORS.glow, transparent: true, opacity: 0.14
    })
  );
  halo.rotation.x = -Math.PI / 2;
  halo.position.y = 0.12;
  g.add(halo);

  // No jardim do distrito a fonte é o MONUMENTO do bairro: cresce p/ preencher o
  // círculo de grama (CityTexture.GARDEN_R).
  if (district) g.scale.setScalar(1.15); // fonte do bairro um pouco maior p/ preencher o jardim (raio 7)

  return g;
}

/* --------------------------- placa / logo --------------------------- */

/* --------------------- painel de LED (marquee) --------------------- */

// Registro dos painéis de LED com texto rolando. Um updater global (main.js →
// updateLedMarquees) avança o offset da textura a cada quadro. O batchStatic funde
// a geometria mas PRESERVA o material/UV, e o offset roda no shader — então a
// rolagem sobrevive à fusão do cenário estático.
const LED_MARQUEES = [];

/** Avança o texto de TODOS os painéis de LED. Chamado 1×/quadro pelo main.js. */
export function updateLedMarquees(dt) {
  for (const m of LED_MARQUEES) {
    m.tex.offset.x = (m.tex.offset.x + m.speed * dt) % 1;
  }
}

/** Registra uma textura (com wrapS=RepeatWrapping) p/ ROLAR junto com os painéis de LED — mesmo
 *  motor de offset no shader, então sobrevive à fusão do cenário estático (ex.: ticker da bolsa). */
export function registerLedMarquee(tex, speed) {
  LED_MARQUEES.push({ tex, speed });
}

/** Textura de painel de LED: a frase em "pixels" verdes acesos sobre fundo escuro,
 *  com efeito DOT-MATRIX (grade de vãos escuros). Larga (1 frase + espaçador) e com
 *  RepeatWrapping p/ rolar na horizontal sem emenda. Devolve { tex, phraseW, H }. */
function makeLedMarqueeTexture(text, color = null) {
  const H = 128;
  const font = '800 84px "Arial Narrow", Inter, Arial, sans-serif';
  // cor do LED: padrão VERDE (rotatória); com `color` (distrito) o ACESO sai na cor do bairro
  // CLAREADA (p/ estourar no bloom) e o APAGADO/grade num tom bem escuro do mesmo matiz.
  const on = color ? mixHex(color, '#ffffff', 0.35) : '#57f58c';
  const glow = color ? mixHex(color, '#ffffff', 0.15) : '#48f584';
  const off = color ? mixHex(color, '#000000', 0.88) : '#04140b';
  const meas = document.createElement('canvas').getContext('2d');
  meas.font = font;
  const phrase = text; // separador (ex.: "   •   ") já vem no texto, simétrico dos 2 lados
  const phraseW = Math.max(64, Math.ceil(meas.measureText(phrase).width));
  const canvas = document.createElement('canvas');
  canvas.width = phraseW; canvas.height = H;
  const c = canvas.getContext('2d');
  c.fillStyle = off; // LED apagado (fundo escuro)
  c.fillRect(0, 0, phraseW, H);
  c.font = font; c.textAlign = 'left'; c.textBaseline = 'middle';
  c.shadowColor = glow; c.shadowBlur = 12;
  c.fillStyle = on; // LED aceso (cor do distrito, ou verde padrão)
  c.fillText(phrase, 8, H / 2 + 3);
  c.shadowBlur = 0;
  // DOT-MATRIX: grade de vãos escuros quebra o texto em "pixels" de LED
  c.fillStyle = off;
  const cell = 8, gap = 2;
  for (let x = 0; x < phraseW; x += cell) c.fillRect(x, 0, gap, H);
  for (let y = 0; y < H; y += cell) c.fillRect(0, y, phraseW, gap);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return { tex, phraseW, H };
}

export function makeWhatsAppSign(cx, cz, scale = 1.1, opts = {}) {
  const { widthMul = 1, heightMul = 1, text = null, color = null } = opts; // text ⇒ LED; color ⇒ cor do distrito
  const g = new THREE.Group();
  g.position.set(cx, 0, cz);

  const PANEL_W = 8.4 * widthMul;  // largura do painel (×widthMul)
  const PANEL_H = 4.6 * heightMul; // altura do painel (×heightMul)
  const MAST_X = 3 * widthMul;     // mastros acompanham a largura (mesmo inset relativo do painel)
  const MAST_TOP = 3.2;            // topo dos mastros = BASE do painel (mastro −20%: o banner baixa junto)
  const MAST_W = 0.28;             // mastros bem mais finos (era 0.5)
  const TILT = -Math.PI / 6;       // 30°: inclina a FACE do painel para cima, a favor da câmera (alta)

  const postMat = new THREE.MeshStandardMaterial({
    color: '#37423a',
    roughness: 0.7,
    metalness: 0.4
  });
  for (const sx of [-MAST_X, MAST_X]) {
    // mastro vai do chão até a base do painel (MAST_TOP) e para ali — não cobre o banner
    const post = new THREE.Mesh(new THREE.BoxGeometry(MAST_W, MAST_TOP, MAST_W), postMat);
    post.position.set(sx, MAST_TOP / 2, 0);
    post.castShadow = true;
    g.add(post);
  }

  // FACE do painel: modo LED (texto rolando) quando `text` é passado; senão, logo do WhatsApp.
  let face;
  if (text != null) {
    const { tex, phraseW, H } = makeLedMarqueeTexture(text, color);
    // repeat.x deixa os "pixels" QUADRADOS no painel (sem distorção): casa a proporção da
    // textura com a do painel. r<1 ⇒ só um trecho da frase aparece por vez (o resto rola).
    tex.repeat.x = (PANEL_W / PANEL_H) * (H / phraseW);
    // rolagem em velocidade constante de MUNDO (~4,5 u/s), independente do tamanho da frase
    const speed = 4.5 * tex.repeat.x / (PANEL_W * scale);
    LED_MARQUEES.push({ tex, speed });
    face = new THREE.MeshStandardMaterial({
      map: tex, emissiveMap: tex, emissive: new THREE.Color('#ffffff'),
      emissiveIntensity: 0.5, roughness: 0.5 // SEM bloom: emissivo abaixo do limiar do bloom (era 1.15)
    });
  } else {
    // Painel: fundo verde + borda + logo OFICIAL do WhatsApp em BRANCO (asset PNG). O canvas
    // acompanha a proporção do painel (CW×CH) para o logo NÃO distorcer quando a altura muda.
    const CW = 512;
    const CH = Math.round(CW * PANEL_H / PANEL_W);
    const canvas = document.createElement('canvas');
    canvas.width = CW;
    canvas.height = CH;
    const c = canvas.getContext('2d');
    c.fillStyle = COLORS.waGreen;
    c.fillRect(0, 0, CW, CH);
    c.strokeStyle = 'rgba(255,255,255,0.85)';
    c.lineWidth = 14;
    c.strokeRect(16, 16, CW - 32, CH - 32);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    // logo branco centrado (sem disco — fiel à marca, branco sobre verde). O PNG carrega
    // assíncrono; ao ficar pronto, desenha no canvas e atualiza a textura.
    const logo = new Image();
    logo.onload = () => {
      const S = Math.round(CH * 0.8); // logo quadrado, limitado pela altura do painel
      c.drawImage(logo, (CW - S) / 2, (CH - S) / 2, S, S);
      tex.needsUpdate = true;
    };
    logo.src = whatsappWhiteUrl;
    face = new THREE.MeshStandardMaterial({
      map: tex,
      emissive: new THREE.Color(COLORS.waGreen),
      emissiveIntensity: 0.18,
      roughness: 0.55
    });
  }
  const edge = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.6 });
  // PIVÔ na base do banner (= topo dos mastros): a inclinação de 30° gira A PARTIR DAQUI, então
  // o painel se abre para cima sem tirar os pés dos mastros do lugar.
  const pivot = new THREE.Group();
  pivot.position.set(0, MAST_TOP, 0);
  pivot.rotation.x = TILT;
  // Painel com CANTOS ARREDONDADOS (border-radius) p/ ficar mais harmônico: retângulo arredondado
  // EXTRUDADO. ExtrudeGeometry → grupo 0 = as duas faces (frente/verso = LED/logo), grupo 1 = as
  // laterais (moldura). Normalizo as UVs p/ 0..1 (o ExtrudeGeometry usa as coords do shape), então
  // a textura do LED (com repeat/offset da rolagem) mapeia igual ao painel retangular.
  const pr = PANEL_H * 0.21; // raio dos cantos −30% (era 0.3), proporcional à altura do painel
  const panelGeo = new THREE.ExtrudeGeometry(roundedRectShape(PANEL_W, PANEL_H, pr), { depth: 0.4, bevelEnabled: false });
  panelGeo.translate(0, 0, -0.2); // centraliza a espessura (como a BoxGeometry fazia)
  const pgPos = panelGeo.attributes.position, pgUv = panelGeo.attributes.uv;
  for (let i = 0; i < pgUv.count; i++) {
    pgUv.setXY(i, pgPos.getX(i) / PANEL_W + 0.5, pgPos.getY(i) / PANEL_H + 0.5);
  }
  const panel = new THREE.Mesh(panelGeo, [face, edge]); // grupo 0 = faces (LED/logo), grupo 1 = laterais (moldura)
  panel.position.set(0, PANEL_H / 2, 0); // base do painel encostada no pivô
  panel.castShadow = true;
  pivot.add(panel);
  g.add(pivot);

  g.scale.setScalar(scale); // 1.1 = padrão (rotatória); parques pedem um banner bem maior
  return g;
}

/* ----------------------- banner do distrito ----------------------- */

/** Shape de retângulo com cantos arredondados (centrado na origem). */
function roundedRectShape(w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/** Canvas com `text` (quebra de linha automática) → CanvasTexture. bg=null → transparente. */
function makeTextTexture(text, cw, ch, { fg = '#233', bg = null, weight = 800, pad = 0.12, maxFont = 200 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = cw; canvas.height = ch;
  const c = canvas.getContext('2d');
  if (bg) { c.fillStyle = bg; c.fillRect(0, 0, cw, ch); }
  c.fillStyle = fg; c.textAlign = 'center'; c.textBaseline = 'middle';
  const maxW = cw * (1 - pad * 2);
  let fs = maxFont;
  const wrap = () => {
    c.font = `${weight} ${fs}px Inter, Arial, sans-serif`;
    const words = text.split(' '); const lines = []; let ln = '';
    for (const w of words) {
      const t = ln ? ln + ' ' + w : w;
      if (c.measureText(t).width > maxW && ln) { lines.push(ln); ln = w; } else ln = t;
    }
    if (ln) lines.push(ln);
    return lines;
  };
  let lines = wrap();
  while ((lines.length * fs * 1.16 > ch * (1 - pad) || lines.some((l) => c.measureText(l).width > maxW)) && fs > 14) {
    fs -= 4; lines = wrap();
  }
  const lh = fs * 1.16;
  const y0 = ch / 2 - ((lines.length - 1) * lh) / 2;
  c.lineWidth = Math.max(2, fs * 0.06); c.lineJoin = 'round'; c.strokeStyle = fg; // contorno = negrito extra
  lines.forEach((l, i) => { const y = y0 + i * lh; c.strokeText(l, cw / 2, y); c.fillText(l, cw / 2, y); });
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Banner do distrito no estilo GATEWAY (ref. do usuário): ARCO tubular na cor do distrito
 *  (U invertido) que passa POR CIMA da fonte central + PLACA SUSPENSA branca (dupla-face,
 *  GRANDE) com o nome, pendurada por 2 tirantes no centro do arco (sobre a fonte). Sem
 *  totem/base (removidos a pedido). */
function makeDistrictBanner(cx, cz, name, tint) {
  const g = new THREE.Group();
  g.position.set(cx, 0, cz);

  const frameMat = new THREE.MeshStandardMaterial({ color: tint, roughness: 0.42, metalness: 0.3 });
  const whiteMat = new THREE.MeshStandardMaterial({ color: '#f4f6f2', roughness: 0.75 });
  const strapMat = new THREE.MeshStandardMaterial({ color: '#cfd2cc', roughness: 0.6, metalness: 0.4 });
  const W = 12, HTOP = 12, TR = 0.5; // W=12: estrutura cabe na largura da área verde (jardim ø~14)

  // ARCO tubular (U invertido) — moldura do distrito (fonte central removida)
  const pts = [
    new THREE.Vector3(-W / 2, 0.15, 0),
    new THREE.Vector3(-W / 2, HTOP - 1.3, 0),
    new THREE.Vector3(-W / 2 + 1.3, HTOP, 0),
    new THREE.Vector3(W / 2 - 1.3, HTOP, 0),
    new THREE.Vector3(W / 2, HTOP - 1.3, 0),
    new THREE.Vector3(W / 2, 0.15, 0)
  ];
  const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0);
  const arch = new THREE.Mesh(new THREE.TubeGeometry(curve, 60, TR, 10, false), frameMat);
  arch.castShadow = true; g.add(arch);
  for (const sx of [-W / 2, W / 2]) {
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(TR * 1.6, TR * 1.9, 0.34, 16), frameMat);
    foot.position.set(sx, 0.17, 0); foot.castShadow = true; g.add(foot);
  }

  // ANEL/PRAÇA no solo: circunferência (curb) na cor do distrito, no RAIO = meia-largura do arco
  // (passa pelos 2 pés e contorna a área → lê como praça circular).
  const ring = new THREE.Mesh(new THREE.TorusGeometry(W / 2, 0.32, 10, 60), frameMat);
  ring.rotation.x = Math.PI / 2; ring.position.y = 0.3; ring.castShadow = true; ring.receiveShadow = true; g.add(ring);

  // ===== CENTERPIECE da praça: medalhão em 3 DEGRAUS preenchendo o verde ATÉ o anel (W/2),
  // pedestal + GEMA brilhante no topo. Baixo o bastante p/ não tapar a placa. =====
  // "água" da fonte: tom CLARO da cor do arco (não branco) — leve brilho + baixa rugosidade
  const waterMat = new THREE.MeshStandardMaterial({ color: mixHex(tint, '#ffffff', 0.58), emissive: new THREE.Color(tint), emissiveIntensity: 0.14, roughness: 0.3, metalness: 0.15 });
  const step1 = new THREE.Mesh(new THREE.CylinderGeometry(W / 2 - 0.5, W / 2 - 0.3, 0.3, 52), waterMat); // ~5.7 ≈ encosta no anel
  step1.position.y = 0.15; step1.castShadow = true; step1.receiveShadow = true; g.add(step1);
  const step2 = new THREE.Mesh(new THREE.CylinderGeometry(W / 2 - 2.0, W / 2 - 1.8, 0.3, 48), frameMat);
  step2.position.y = 0.45; step2.castShadow = true; g.add(step2);
  const step3 = new THREE.Mesh(new THREE.CylinderGeometry(W / 2 - 3.6, W / 2 - 3.4, 0.3, 40), waterMat);
  step3.position.y = 0.75; step3.castShadow = true; g.add(step3);
  const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.8, 1.15, 20), frameMat);
  ped.position.y = 1.48; ped.castShadow = true; g.add(ped);
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.66, 0.66, 0.2, 20), waterMat);
  collar.position.y = 2.15; g.add(collar);
  const gem = new THREE.Mesh(new THREE.IcosahedronGeometry(0.9, 0), new THREE.MeshStandardMaterial({ color: mixHex(tint, '#ffffff', 0.35), emissive: new THREE.Color(tint), emissiveIntensity: 0.95, roughness: 0.3, metalness: 0.2 }));
  gem.position.y = 2.5; gem.rotation.y = 0.4; gem.castShadow = true; g.add(gem);

  // PLACA GRANDE, inclinada p/ a câmera AÉREA (fonte removida → cabe bem maior e mais legível)
  const HSY = 6.8, HSW = 11, HSH = 4.6, TILT = 0; // placa VERTICAL (90° com o solo) — sem inclinação
  const sign = new THREE.Group();
  // BORDA na cor do arco (moldura) + painel BRANCO menor por cima → a borda colorida destaca a placa (2 faces)
  const border = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedRectShape(HSW, HSH, 0.7), { depth: 0.3, bevelEnabled: false }), frameMat);
  border.position.z = -0.15; border.castShadow = true; sign.add(border);
  const signBody = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedRectShape(HSW - 0.7, HSH - 0.7, 0.45), { depth: 0.46, bevelEnabled: false }), whiteMat);
  signBody.position.z = -0.23; sign.add(signBody);
  const nameMat = new THREE.MeshStandardMaterial({ map: makeTextTexture(name, 1024, 400, { fg: '#202b26', weight: 900, pad: 0.08 }), transparent: true, roughness: 0.7 });
  for (const sz of [0.25, -0.25]) {
    const txt = new THREE.Mesh(new THREE.PlaneGeometry(HSW - 1.0, HSH - 0.9), nameMat);
    txt.position.set(0, 0, sz); if (sz < 0) txt.rotation.y = Math.PI; sign.add(txt);
  }
  sign.position.set(0, HSY, 0); sign.rotation.x = TILT; g.add(sign);
  // 2 tirantes do beam até o topo da placa (vertical, no plano do arco)
  const topY = HSY + (HSH / 2) * Math.cos(TILT);
  const topZ = (HSH / 2) * Math.sin(TILT);
  const strapLen = HTOP - topY;
  for (const sx of [-3.7, 3.7]) {
    const strap = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, strapLen, 8), strapMat);
    strap.position.set(sx, topY + strapLen / 2, topZ); g.add(strap);
  }

  return g;
}

/* ------------------------------ banco ------------------------------ */

export function makeBench() {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: '#7a5230', roughness: 0.85 });
  const metal = new THREE.MeshStandardMaterial({ color: '#1d3a26', roughness: 0.7 });
  const seat = new THREE.Mesh(new THREE.BoxGeometry(3, 0.2, 1.0), wood);
  seat.position.y = 0.8;
  seat.castShadow = true;
  const back = new THREE.Mesh(new THREE.BoxGeometry(3, 0.9, 0.18), wood);
  back.position.set(0, 1.3, -0.42);
  back.castShadow = true;
  for (const sx of [-1.3, 1.3]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.25, 0.8, 1.0), metal);
    leg.position.set(sx, 0.4, 0);
    g.add(leg);
  }
  g.add(seat, back);
  return g;
}
